import { assignmentTeachingInstrument } from "@wmf/shared/teachingInstruments";
import type { TeachingAssignment } from "../api.js";

const ALL_LESSON_LENGTHS = "All available lesson lengths";

export interface TeachingAssignmentGroup {
  key: string;
  tutorName: string | null;
  instrument: string | null;
  lessonLengths: string[];
  assignments: TeachingAssignment[];
}

function displayInstrument(assignment: TeachingAssignment): string | null {
  // The live online catalogue uses a trailing duration. Normalise that label
  // for display only; the booking authority's service grammar stays unchanged.
  const name = assignment.serviceName?.trim().replace(/\s+/g, " ");
  const online = name && /^online (.+?) lessons? \((\d+)\s*min(?:ute)?s?\)$/i.exec(name);
  return assignmentTeachingInstrument({
    instrument: assignment.instrument,
    serviceName: online ? `${online[2]} Minute ${online[1]} Lesson` : assignment.serviceName,
  });
}

function lessonLength(assignment: TeachingAssignment, instrument: string | null): string {
  if (instrument && assignment.serviceId === null && assignment.serviceName === null) {
    return ALL_LESSON_LENGTHS;
  }
  const name = assignment.serviceName?.trim();
  const minutes = instrument && name
    ? /^(\d+)\s*(?:minute|min)\b/i.exec(name)?.[1]
      ?? /\((\d+)\s*min(?:ute)?s?\)$/i.exec(name)?.[1]
    : null;
  const blockCount = name && /\b(\d+)-lesson block\b/i.exec(name)?.[1];
  return minutes
    ? `${Number(minutes)} minutes${blockCount ? ` (${Number(blockCount)}-lesson block)` : ""}`
    : name || assignment.instrument || "Teaching scope recorded";
}

function compareLessonLengths(a: string, b: string): number {
  if (a === b) return 0;
  if (a === ALL_LESSON_LENGTHS) return 1;
  if (b === ALL_LESSON_LENGTHS) return -1;
  // Numeric durations come first; named scopes follow; instrument-wide
  // coverage is always last. This order does not depend on locale collation.
  const minutesA = Number(/^(\d+) minutes\b/.exec(a)?.[1] ?? Infinity);
  const minutesB = Number(/^(\d+) minutes\b/.exec(b)?.[1] ?? Infinity);
  if (minutesA !== minutesB) return minutesA - minutesB;
  const blockA = Number(/\((\d+)-lesson block\)$/.exec(a)?.[1] ?? 0);
  const blockB = Number(/\((\d+)-lesson block\)$/.exec(b)?.[1] ?? 0);
  if (blockA !== blockB) return blockA - blockB;
  return a < b ? -1 : 1;
}

/** Presentation only: each original scope and version remains independently manageable. */
export function groupTeachingAssignments(assignments: TeachingAssignment[]): TeachingAssignmentGroup[] {
  const groups = new Map<string, TeachingAssignmentGroup>();
  for (const assignment of assignments) {
    const instrument = displayInstrument(assignment);
    // Unrecognised or contradictory scopes stay separate rather than being
    // presented as the same instrument. Display names never identify a tutor.
    const key = JSON.stringify([
      assignment.studentId,
      assignment.tutorProfileId,
      assignment.verifiedProviderId,
      instrument ?? assignment.id,
    ]);
    let group = groups.get(key);
    if (!group) {
      group = { key, tutorName: assignment.tutorName?.trim() || null, instrument, lessonLengths: [], assignments: [] };
      groups.set(key, group);
    }
    // Names do not identify the teacher; keep the first available name when
    // rows have missing or differently formatted display names.
    if (!group.tutorName && assignment.tutorName?.trim()) group.tutorName = assignment.tutorName.trim();
    group.assignments.push(assignment);
    const length = lessonLength(assignment, instrument);
    if (!group.lessonLengths.includes(length)) group.lessonLengths.push(length);
  }
  for (const group of groups.values()) {
    group.lessonLengths.sort(compareLessonLengths);
  }
  return [...groups.values()];
}
